import { Controller } from "@hotwired/stimulus"

// This controller handles showing/hiding the LoRA field section 
// when the style dropdown is set to "pony"
export default class extends Controller {
  static targets = ["loraFields", "loraContainer", "loraField", "addButton"]
  
  // Constants
  MAX_LORA_FIELDS = 7

  connect() {
    // console.log("PonyStyleController connected")
    // console.log("Element:", this.element)
    // console.log("Controller scope HTML:", this.element.outerHTML.substring(0, 100) + '...')
    // console.log("Has loraFieldsTarget:", this.hasLoraFieldsTarget)
    // console.log("Has loraContainerTarget:", this.hasLoraContainerTarget)
    // console.log("LoraField count:", this.loraFieldTargets.length)
    
    // When controller connects, check if style is already "pony"
    this.toggleLoraFields()
    
    // Check if we need to disable the add button
    this.updateAddButtonState()
  }

  toggleLoraFields(event) {
    // console.log("toggleLoraFields called", event)
    
    // Get the current selected style value
    const styleSelect = this.element.querySelector('select[name="image_generation_task[style]"]')
    const currentValue = styleSelect ? styleSelect.value : null
    const isPonyStyle = currentValue === "pony"
    
    // console.log("Found style select:", !!styleSelect)
    // console.log("Current style value:", currentValue)
    // console.log("isPonyStyle:", isPonyStyle)
    
    // Show/hide the LoRA fields based on style
    if (this.hasLoraFieldsTarget) {
    //   console.log("Toggling visibility of LoRA fields")
      if (isPonyStyle) {
        // console.log("Showing LoRA fields")
        this.loraFieldsTarget.classList.remove("hidden")
      } else {
        // console.log("Hiding LoRA fields")
        this.loraFieldsTarget.classList.add("hidden")
      }
    } else {
    //   console.log("ERROR: loraFieldsTarget not found!")
    }
  }
  
  addLoraField(event) {
    event.preventDefault()
    
    // Check if we've reached the maximum number of fields
    if (this.loraFieldTargets.length >= this.MAX_LORA_FIELDS) {
    //   console.log("Maximum number of LoRA fields reached")
      return
    }
    
    // Create a new field by cloning the template and updating the index
    const nextIndex = this.loraFieldTargets.length
    
    // Use Rails.ajax to fetch the partial with the correct index
    fetch(`/generate/new_lora_field?index=${nextIndex}`, {
      headers: {
        'Accept': 'text/html',
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => response.text())
    .then(html => {
      // Append the new field to the container
      this.loraContainerTarget.insertAdjacentHTML('beforeend', html)
      
      // Update the add button state
      this.updateAddButtonState()
      
      // Update all field numbers
      this.updateFieldNumbers()
    })
    .catch(error => {
      console.error("Error fetching LoRA field template:", error)
      
      // If we have an existing field to clone
      if (this.loraFieldTargets.length > 0) {
        const template = this.loraFieldTargets[0].cloneNode(true)
        
        // Update the index in the title
        const titleElement = template.querySelector('.lora-number')
        if (titleElement) {
          titleElement.textContent = nextIndex + 1
        }
        
        // Clear input values
        template.querySelectorAll('input').forEach(input => {
          if (input.type === 'number') {
            input.value = 0.5
          } else {
            input.value = ''
          }
        })
        
        // Make sure the remove button is visible
        const removeButton = template.querySelector('button[data-action="click->pony-style#removeLoraField"]')
        if (!removeButton) {
          const titleContainer = template.querySelector('h5').parentElement
          
          const removeButtonHTML = `
            <button type="button" 
                    class="text-red-400 hover:text-red-300 transition-colors duration-150"
                    data-action="click->pony-style#removeLoraField">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
              </svg>
            </button>
          `
          
          titleContainer.insertAdjacentHTML('beforeend', removeButtonHTML)
        }
        
        // Add the new field to the container
        this.loraContainerTarget.appendChild(template)
      } else {
        // If there are no fields to clone, create one from scratch
        const fallbackHTML = `
          <div class="mb-4 p-3 bg-gray-850 border border-gray-650 rounded-lg lora-field" data-pony-style-target="loraField">
            <div class="flex justify-between items-center mb-2">
              <h5 class="text-sm font-medium text-gray-400">LoRA #<span class="lora-number">1</span></h5>
            </div>
            
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">URL</label>
                <input type="text" 
                      name="image_generation_task[fal_lora_configs][][url]" 
                      class="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      value="">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">Strength</label>
                <input type="number" 
                      name="image_generation_task[fal_lora_configs][][strength]" 
                      class="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      step="0.1" 
                      min="0" 
                      max="2.0" 
                      value="0.5">
              </div>
            </div>
          </div>
        `
        
        this.loraContainerTarget.insertAdjacentHTML('beforeend', fallbackHTML)
      }
      
      // Update the add button state
      this.updateAddButtonState()
      
      // Update all field numbers
      this.updateFieldNumbers()
    })
  }
  
  removeLoraField(event) {
    event.preventDefault()
    
    // Get the LoRA field containing the clicked button
    const field = event.target.closest('.lora-field')
    if (field) {
      // Remove the field
      field.remove()
      
      // Update the add button state
      this.updateAddButtonState()
      
      // Update all field numbers
      this.updateFieldNumbers()
    }
  }
  
  updateAddButtonState() {
    if (this.hasAddButtonTarget) {
      if (this.loraFieldTargets.length >= this.MAX_LORA_FIELDS) {
        this.addButtonTarget.disabled = true
        this.addButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
        this.addButtonTarget.classList.remove('hover:bg-blue-700')
      } else {
        this.addButtonTarget.disabled = false
        this.addButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
        this.addButtonTarget.classList.add('hover:bg-blue-700')
      }
    }
  }
  
  updateFieldNumbers() {
    this.loraFieldTargets.forEach((field, index) => {
      const numberElement = field.querySelector('.lora-number')
      if (numberElement) {
        numberElement.textContent = index + 1
      }
    })
  }
} 